import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, Avatar } from '@mui/material';
import type { MarkPriceData, FuturesOrderBook } from '../../services/futures/types';
import type { PriceLevel } from '../../types/opportunity';
import { formatPrice } from '../../utils/formatters';
import {
  getFundingRateColor,
  getFullPrecisionFundingRate,
} from '../../utils/futuresFormatters';
import { getExchangeLogo } from '../../utils/exchangeUrls';
import { usePriceUpdates } from '../../hooks/usePriceUpdates';

interface FuturesDataRowProps {
  /** Unified symbol format (e.g., "BTC/USDT") */
  symbol: string;

  /** Mark price data - undefined if unavailable or stale */
  markPriceData?: MarkPriceData;

  /** Futures orderbook - undefined if unavailable or stale */
  futuresOrderbook?: FuturesOrderBook;

  /** Spot buy levels (bids) to calculate price difference */
  spotBuyLevels?: PriceLevel[];

  /** Quote currency enum of buy levels (0=USDT, 1=KRW, 2=BTC) */
  buyQuote: string;

  /** Cell style for consistent spacing */
  cellStyle?: React.CSSProperties;
}

/**
 * Extract base and quote currencies from unified symbol format
 * @param symbol - Unified symbol format (e.g., "BTC/USDT")
 * @returns { base, quote } - Base and quote currencies
 */
function parseSymbol(symbol: string): { base: string; quote: string } {
  const [base, quote] = symbol.split('/');
  return { base: base || '', quote: quote || '' };
}

/**
 * Generate Binance futures URL for a trading pair
 * @param symbol - Unified symbol format (e.g., "BTC/USDT")
 * @returns Binance futures URL
 */
function getBinanceFuturesUrl(symbol: string): string {
  const { base, quote } = parseSymbol(symbol);
  return `https://www.binance.com/en/futures/${base}${quote}`;
}

/**
 * Format countdown timer as HH:MM:SS
 */
function formatCountdown(targetTimestamp: number): string {
  const now = Date.now();
  const diff = Math.max(0, targetTimestamp - now);

  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * FuturesDataRow Component
 *
 * Displays Binance Futures market data in a row format matching the existing
 * OpportunityRowSection layout:
 * - Column 1 (35.29%): Price difference percentage with Binance logo
 * - Column 2 (32.35%): Futures best bid price
 * - Column 3 (32.35%): Funding rate (5 decimals) with countdown timer (HH:MM:SS)
 *
 * @component
 */
export const FuturesDataRow: React.FC<FuturesDataRowProps> = React.memo(
  ({ symbol, markPriceData, futuresOrderbook, spotBuyLevels, buyQuote, cellStyle }) => {
    // Show row if we have either mark price data OR futures orderbook
    if (!markPriceData && !futuresOrderbook) {
      return null;
    }

    // Handler to open Binance futures in new tab
    const handleClickFuturesLink = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent row click event
      const url = getBinanceFuturesUrl(symbol);
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    // State for countdown timer (updates every second)
    const [countdown, setCountdown] = useState<string>('00:00:00');

    // Get price data for conversion rates
    const prices = usePriceUpdates();

    // Extract futures data
    const fundingRate = markPriceData?.fundingRate;
    const nextFundingTime = markPriceData?.nextFundingTime;

    // Get futures best bid - prefer bestBid from mark price data (book ticker stream)
    // Fallback to orderbook bids if available (for backwards compatibility)
    const futuresBestBid = markPriceData?.bestBid && markPriceData.bestBid > 0
      ? markPriceData.bestBid
      : futuresOrderbook?.bids?.[0]?.[0]; // [price, volume] fallback

    // Get spot best buy price (first buy level - highest bid)
    const spotBestBuyPrice = spotBuyLevels?.[0]?.price;

    // Convert spot price to USDT based on quote currency
    // buyQuote: 0=USDT, 1=KRW, 2=BTC
    // Use actual conversion rates from HeaderStats (upbitBtcKrw / binanceBtcUsdt for KRW, binanceBtcUsdt for BTC)
    const spotPriceInUSDT = spotBestBuyPrice && prices
      ? buyQuote === 'U'
        ? spotBestBuyPrice // Already in USDT
        : buyQuote === 'K' && prices.upbitBtcKrw && prices.binanceBtcUsdt
        ? spotBestBuyPrice / (prices.upbitBtcKrw / prices.binanceBtcUsdt) // KRW: convert using actual KRW/USDT rate
        : buyQuote === 'B' && prices.binanceBtcUsdt
        ? spotBestBuyPrice * prices.binanceBtcUsdt // BTC: convert using actual BTC/USDT price
        : undefined
      : undefined;

    // Calculate price difference percentage: (spot_ask - futures_bid) / spot_ask * 100
    const priceDifferencePercentage = futuresBestBid && spotPriceInUSDT
      ? ((spotPriceInUSDT - futuresBestBid) / spotPriceInUSDT) * 100
      : undefined;

    // Format funding rate with 5 decimal places
    const formattedFundingRate = fundingRate !== undefined
      ? `${(fundingRate * 100).toFixed(5)}%`
      : 'N/A';
    const fundingRateColor = fundingRate !== undefined
      ? getFundingRateColor(fundingRate)
      : 'rgba(255, 255, 255, 0.5)';
    const fullPrecisionRate = fundingRate !== undefined
      ? getFullPrecisionFundingRate(fundingRate)
      : 'N/A';

    // Update countdown every second
    useEffect(() => {
      if (!nextFundingTime || nextFundingTime === 0) {
        setCountdown('00:00:00');
        return;
      }

      // Update immediately
      setCountdown(formatCountdown(nextFundingTime));

      // Then update every second
      const interval = setInterval(() => {
        setCountdown(formatCountdown(nextFundingTime));
      }, 1000);

      return () => clearInterval(interval);
    }, [nextFundingTime]);

    return (
      <Box display="flex">
        {/* Column 1: Price difference percentage with Binance logo - 35.29% (clickable) */}
        <Tooltip
          title={
            futuresBestBid && spotPriceInUSDT && prices
              ? buyQuote === 'U'
                ? `Click to open Binance Futures | Price Difference: (${spotPriceInUSDT.toFixed(2)} - ${futuresBestBid.toFixed(2)}) / ${spotPriceInUSDT.toFixed(2)} × 100 = ${priceDifferencePercentage?.toFixed(2)}%`
                : buyQuote === 'K' && prices.upbitBtcKrw && prices.binanceBtcUsdt
                ? `Click to open Binance Futures | Price Difference (KRW converted): Spot ${spotBestBuyPrice?.toFixed(0)} KRW ÷ (${prices.upbitBtcKrw.toFixed(0)} / ${prices.binanceBtcUsdt.toFixed(2)}) = ${spotPriceInUSDT.toFixed(2)} USDT | (${spotPriceInUSDT.toFixed(2)} - ${futuresBestBid.toFixed(2)}) / ${spotPriceInUSDT.toFixed(2)} × 100 = ${priceDifferencePercentage?.toFixed(2)}%`
                : buyQuote === 'B' && prices.binanceBtcUsdt
                ? `Click to open Binance Futures | Price Difference (BTC converted): Spot ${spotBestBuyPrice?.toFixed(8)} BTC × ${prices.binanceBtcUsdt.toFixed(2)} = ${spotPriceInUSDT.toFixed(2)} USDT | (${spotPriceInUSDT.toFixed(2)} - ${futuresBestBid.toFixed(2)}) / ${spotPriceInUSDT.toFixed(2)} × 100 = ${priceDifferencePercentage?.toFixed(2)}%`
                : 'Conversion rate unavailable'
              : 'Price difference calculation unavailable'
          }
          arrow
          placement="top"
        >
          <Box
            onClick={handleClickFuturesLink}
            sx={{
              ...cellStyle,
              width: '35.29%',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <Avatar
                src={getExchangeLogo(3)} // 3 = Binance index
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  filter: 'grayscale(100%) brightness(1.8)', // Brighter grayscale to make it more white
                  opacity: 0.95,
                }}
              >
                B
              </Avatar>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', md: '0.85rem' },
                  color: priceDifferencePercentage !== undefined
                    ? priceDifferencePercentage < 0
                      ? '#4caf50'
                      : priceDifferencePercentage > 0
                      ? '#ff9800'
                      : 'rgba(255, 255, 255, 0.87)'
                    : 'rgba(255, 255, 255, 0.5)',
                  fontWeight: 500,
                }}
              >
                {priceDifferencePercentage !== undefined ? `${priceDifferencePercentage.toFixed(2)}%` : 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Tooltip>

        {/* Column 2: Futures best bid price - 32.35% */}
        <Tooltip
          title={
            futuresBestBid !== undefined
              ? `Futures Best Bid: ${formatPrice(futuresBestBid, 'U')}`
              : 'Futures orderbook unavailable'
          }
          arrow
          placement="top"
        >
          <Box sx={{ ...cellStyle, width: '32.35%', justifyContent: 'flex-end', pr: 1, cursor: 'help' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
              {futuresBestBid !== undefined ? formatPrice(futuresBestBid, 'U') : 'N/A'}
            </Typography>
          </Box>
        </Tooltip>

        {/* Column 3: Funding rate and countdown timer - 32.35% */}
        <Tooltip title={`Funding Rate: ${fullPrecisionRate}`} arrow placement="top">
          <Box sx={{ ...cellStyle, width: '32.35%', cursor: 'help' }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.8rem', md: '0.95rem' },
                  color: fundingRateColor,
                  fontWeight: 500,
                }}
              >
                {formattedFundingRate}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: '0.65rem', md: '0.8rem' },
                  color: 'rgba(255, 255, 255, 0.45)' // Match OpportunityRow elapsed time color
                }}
              >
                / {countdown}
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      </Box>
    );
  }
);

FuturesDataRow.displayName = 'FuturesDataRow';
